// src/views/Datatable.js
import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useHistory, useLocation } from 'react-router-dom';

import '../assets/css/DatatableFilterModel.css';
import { sraUrl, apiPath } from '../apiurl.js';

import {
  EMPTY_FILTERS,
  TIME_FRAMES,
  PAGE_SIZES,
  COLUMN_DEFS,
  TOOL_CHECKBOXES,
  isEmpty,
  nested,
  hasToolData,
  fmtDate,
  buildQueryParams,
  buildAggregateParams,
  formatBucketLabel,
  fillGaps,
  granularityLabel,
  countActiveFilters,
  buildFilterTags,
  buildPageButtons,
} from '../utils/DatatableFilterModel.js';
import { makeAbortable, ignoreAbort } from '../utils/errorGuard.js';

// ─────────────────────────────────────────────────────────────────────────────
//  FILTER MODAL
// ─────────────────────────────────────────────────────────────────────────────

const BLAST_REGIONS = ['5UTR','VP4','VP3','VP2','VP1','2A','2B','2C','3A','3B','3C','3D'];

// ── Multi-select dropdown for metadata fields ──────────────────────────────
function MetaMultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) { setSearch(''); return; }
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  // Selected items always shown first (pinned), then unselected matches below.
  // This keeps the user's choices visible even when the search term changes.
  const lowerSearch = search.toLowerCase();
  const selectedPinned  = selected.filter(o => !search || o.toLowerCase().includes(lowerSearch));
  const unselectedBelow = options.filter(o => !selected.includes(o) && (!search || o.toLowerCase().includes(lowerSearch)));
  const hasResults = selectedPinned.length > 0 || unselectedBelow.length > 0;

  return (
    <div className="mms-wrap" ref={ref}>
      <button type="button" className={`mms-trigger ${selected.length > 0 ? 'mms-trigger--active' : ''}`} onClick={() => setOpen(v => !v)}>
        {selected.length === 0 ? <span className="mms-placeholder">Any {label}</span> : (
          <span className="mms-selected-label">{selected.length === 1 ? selected[0] : `${selected.length} selected`}</span>
        )}
        <span className="mms-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mms-dropdown">
          <input
            className="mms-search"
            placeholder={`Search ${label}…`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <div className="mms-list">
            {!hasResults && <div className="mms-empty">No matches</div>}
            {selectedPinned.map(opt => (
              <label key={opt} className="mms-option mms-option--selected">
                <input type="checkbox" checked onChange={() => toggle(opt)} />
                <span className="mms-opt-label" title={opt}>{opt}</span>
              </label>
            ))}
            {selectedPinned.length > 0 && unselectedBelow.length > 0 && (
              <div className="mms-divider" />
            )}
            {unselectedBelow.map(opt => (
              <label key={opt} className="mms-option">
                <input type="checkbox" checked={false} onChange={() => toggle(opt)} />
                <span className="mms-opt-label" title={opt}>{opt}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <button type="button" className="mms-clear" onClick={() => { onChange([]); setSearch(''); }}>
              Clear {label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FilterModal({ open, onClose, filters, onApply, totalCount, metaOptions = {}, fetchPreviewCount }) {
  const [local,        setLocal]        = useState(filters);
  const [liveCount,    setLiveCount]    = useState(null);
  const [liveLoading,  setLiveLoading]  = useState(false);

  useEffect(() => {
    if (open) { setLocal(filters); setLiveCount(null); }
  }, [open, filters]);

  // Debounced live preview — fires 500 ms after the user stops changing local filters.
  useEffect(() => {
    if (!open || !fetchPreviewCount) return;
    setLiveLoading(true);
    const timer = setTimeout(async () => {
      try {
        const count = await fetchPreviewCount(local);
        setLiveCount(count);
      } catch { /* non-fatal */ }
      finally { setLiveLoading(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [local, open]); // fetchPreviewCount intentionally omitted — stable callback from parent

  if (!open) return null;

  const set    = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const toggle = (k)    => setLocal((p) => ({ ...p, [k]: !p[k] }));
  const toggleRegion = (r) => setLocal((p) => {
    const current = p.blastNRegions ?? [];
    return {
      ...p,
      blastNRegions: current.includes(r)
        ? current.filter((x) => x !== r)
        : [...current, r],
    };
  });

  const activeCount   = countActiveFilters(local);
  const activeRegions = (local.blastNRegions ?? []).length;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box fp-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="fp-header">
          <div className="fp-header-left">
            <div className="fp-header-icon-wrap">⚡</div>
            <div>
              <div className="fp-header-title">Filter Panel</div>
              <div className="fp-header-sub">
                {activeCount > 0 ? `${activeCount} active filter${activeCount > 1 ? 's' : ''}` : 'No active filters'}
              </div>
            </div>
          </div>
          <button className="modal-close fp-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Body: 2-column ── */}
        <div className="modal-body fp-body">

          {/* Left column — identifier · dates · numeric ranges */}
          <div className="fp-col fp-col-left">

            <div className="fp-section">
              <div className="fp-section-title">Run Identifier</div>
              <input
                className="filter-input"
                placeholder="Search by Run ID substring…"
                value={local.runId}
                onChange={(e) => set('runId', e.target.value)}
              />
            </div>

            <div className="fp-section">
              <div className="fp-section-title">Date Ranges</div>
              <div className="fp-field-group">
                <div className="fp-field-label">Run Start</div>
                <div className="filter-range">
                  <input type="date" className="filter-input" value={local.runStartFrom} onChange={(e) => set('runStartFrom', e.target.value)} />
                  <span className="filter-range-sep">to</span>
                  <input type="date" className="filter-input" value={local.runStartTo}   onChange={(e) => set('runStartTo',   e.target.value)} />
                </div>
                <div className="fp-field-label">Run End</div>
                <div className="filter-range">
                  <input type="date" className="filter-input" value={local.runEndFrom} onChange={(e) => set('runEndFrom', e.target.value)} />
                  <span className="filter-range-sep">to</span>
                  <input type="date" className="filter-input" value={local.runEndTo}   onChange={(e) => set('runEndTo',   e.target.value)} />
                </div>
                <div className="fp-field-label">SRA Release</div>
                <div className="filter-range">
                  <input type="date" className="filter-input" value={local.sraRelFrom} onChange={(e) => set('sraRelFrom', e.target.value)} />
                  <span className="filter-range-sep">to</span>
                  <input type="date" className="filter-input" value={local.sraRelTo}   onChange={(e) => set('sraRelTo',   e.target.value)} />
                </div>
              </div>
            </div>

            <div className="fp-section">
              <div className="fp-section-title">Numeric Ranges</div>
              <div className="fp-field-group">
                <div className="fp-field-label">Elapsed Time (sec)</div>
                <div className="filter-range">
                  <input type="number" className="filter-input" placeholder="Min" value={local.elapsedMin} onChange={(e) => set('elapsedMin', e.target.value)} />
                  <span className="filter-range-sep">–</span>
                  <input type="number" className="filter-input" placeholder="Max" value={local.elapsedMax} onChange={(e) => set('elapsedMax', e.target.value)} />
                </div>
                <div className="fp-field-label">File Size (bytes)</div>
                <div className="filter-range">
                  <input type="number" className="filter-input" placeholder="Min" value={local.sizeMin} onChange={(e) => set('sizeMin', e.target.value)} />
                  <span className="filter-range-sep">–</span>
                  <input type="number" className="filter-input" placeholder="Max" value={local.sizeMax} onChange={(e) => set('sizeMax', e.target.value)} />
                </div>
                <div className="fp-field-label">Bowtie2 ReadHits</div>
                <div className="filter-range">
                  <input type="number" className="filter-input" placeholder="Min" value={local.bt2HitsMin} onChange={(e) => set('bt2HitsMin', e.target.value)} />
                  <span className="filter-range-sep">–</span>
                  <input type="number" className="filter-input" placeholder="Max" value={local.bt2HitsMax} onChange={(e) => set('bt2HitsMax', e.target.value)} />
                </div>
                <div className="fp-field-label">BlastN RegionHits</div>
                <div className="filter-range">
                  <input type="number" className="filter-input" placeholder="Min" value={local.blastNHitsMin} onChange={(e) => set('blastNHitsMin', e.target.value)} />
                  <span className="filter-range-sep">–</span>
                  <input type="number" className="filter-input" placeholder="Max" value={local.blastNHitsMax} onChange={(e) => set('blastNHitsMax', e.target.value)} />
                </div>
              </div>
            </div>

          </div>

          {/* Right column — genomic regions · tool toggles */}
          <div className="fp-col fp-col-right">

            <div className="fp-section">
              <div className="fp-section-title">
                BlastN Genomic Regions
                {activeRegions > 0 && (
                  <span className="fp-section-actions">
                    <span className="fp-section-badge">{activeRegions}</span>
                    <button type="button" className="fp-clear-link" onClick={() => set('blastNRegions', [])}>Clear</button>
                  </span>
                )}
              </div>
              <div className="fp-chip-grid">
                {BLAST_REGIONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`fp-chip${(local.blastNRegions ?? []).includes(r) ? ' fp-chip--active' : ''}`}
                    onClick={() => toggleRegion(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
              {activeRegions > 0 && (
                <div className="fp-chip-hint">{(local.blastNRegions ?? []).join(' · ')}</div>
              )}
            </div>

            <div className="fp-section">
              <div className="fp-section-title">Require Tool Data</div>
              <div className="fp-chip-grid fp-chip-grid--tools">
                {TOOL_CHECKBOXES.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`fp-chip fp-chip--tool${local[key] ? ' fp-chip--active' : ''}`}
                    onClick={() => toggle(key)}
                  >
                    {local[key] && <span className="fp-chip-check">✓</span>}
                    {label}
                  </button>
                ))}
              </div>
            </div>

          </div>

          {/* Full-width — Metadata */}
          <div className="fp-full">
            <div className="fp-section-title fp-meta-title">🧬 Metadata</div>
            <div className="fp-meta-grid">
              <div>
                <div className="fp-field-label">Name</div>
                <MetaMultiSelect label="Name" options={metaOptions.names || []} selected={local.metaName || []} onChange={v => set('metaName', v)} />
              </div>
              <div>
                <div className="fp-field-label">Organism</div>
                <MetaMultiSelect label="Organism" options={metaOptions.organisms || []} selected={local.metaOrganism || []} onChange={v => set('metaOrganism', v)} />
              </div>
              <div>
                <div className="fp-field-label">Platform</div>
                <MetaMultiSelect label="Platform" options={metaOptions.platforms || []} selected={local.metaPlatform || []} onChange={v => set('metaPlatform', v)} />
              </div>
              <div>
                <div className="fp-field-label">Country</div>
                <MetaMultiSelect label="Country" options={metaOptions.countries || []} selected={local.metaCountry || []} onChange={v => set('metaCountry', v)} />
              </div>
              <div>
                <div className="fp-field-label">Continent</div>
                <MetaMultiSelect label="Continent" options={metaOptions.continents || []} selected={local.metaContinent || []} onChange={v => set('metaContinent', v)} />
              </div>
              <div>
                <div className="fp-field-label">Isolation Source</div>
                <MetaMultiSelect label="Iso Source" options={metaOptions.isolationSources || []} selected={local.metaIsoSource || []} onChange={v => set('metaIsoSource', v)} />
              </div>
              <div className="fp-meta-span">
                <div className="fp-field-label">Center Name</div>
                <MetaMultiSelect label="Center" options={metaOptions.centers || []} selected={local.metaCenter || []} onChange={v => set('metaCenter', v)} />
              </div>
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="modal-footer">
          <div className="filter-count">
            {liveLoading ? (
              <span style={{ opacity: 0.6 }}>Checking…</span>
            ) : liveCount !== null ? (
              <>Matching: <strong>{liveCount.toLocaleString()}</strong> / {totalCount.toLocaleString()} rows</>
            ) : (
              <>Current: <strong>{totalCount.toLocaleString()}</strong> rows</>
            )}
          </div>
          <div className="modal-footer-actions">
            <button className="btn-reset" onClick={() => setLocal(EMPTY_FILTERS)}>Reset All</button>
            <button className="btn-apply" onClick={() => { onApply(local); onClose(); }}>Apply Filters</button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PREVIEW MODAL — helpers
// ─────────────────────────────────────────────────────────────────────────────

function fmtFull(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

function fmtSeconds(s) {
  const sec = parseFloat(s);
  if (isNaN(sec)) return String(s);
  if (sec < 60) return `${sec.toFixed(2)}s`;
  return `${Math.floor(sec / 60)}m ${(sec % 60).toFixed(0)}s`;
}

function fmtBytes(b) {
  const n = parseFloat(b);
  if (isNaN(n) || n === 0) return '0 B';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)} KB`;
  return `${n} B`;
}

function taskStatusClass(status) {
  const s = (status ?? '').toLowerCase();
  if (s === 'finished') return 'task-status-finished';
  if (s === 'failed')   return 'task-status-failed';
  if (s === 'running')  return 'task-status-running';
  return 'task-status-unknown';
}

function CollapsibleJSON({ label, data }) {
  const [open, setOpen] = useState(false);
  if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) return null;
  return (
    <div className="pv-collapsible">
      <button className="pv-collapsible-trigger" onClick={() => setOpen((o) => !o)}>
        <span className="pv-collapsible-arrow">{open ? '▾' : '▸'}</span>
        {label}
      </button>
      {open && <pre className="pv-json-block">{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PREVIEW MODAL
// ─────────────────────────────────────────────────────────────────────────────

function PreviewModal({ row, onClose }) {
  if (!row) return null;

  const download = () => {
    const blob = new Blob([JSON.stringify(row, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `run_${row.Run}.json`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const bowtie2Anno  = row.bowtie2?.PolioAnno            ?? {};
  const seqtkAnno    = row['seqtk(bowtie2)']?.PolioAnno  ?? {};
  const blastNAnno   = row['blastN(bowtie2)']?.PolioAnno ?? {};
  const blastNSPAdes = row['blastN(SPAdes)']             ?? null;
  const spades       = row.SPAdes                        ?? null;
  const samtools     = row.samtools                      ?? null;
  const regionData   = blastNAnno.RegionHitsData         ?? null;

  const workflowAttempts = row.Workflow ? Object.values(row.Workflow) : [];
  const latestAttempt    = workflowAttempts[workflowAttempts.length - 1] ?? null;
  const tasks            = latestAttempt?.Tasks ? Object.entries(latestAttempt.Tasks) : [];

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box preview-modal-rich" role="dialog" aria-modal="true">
        <div className="modal-header pv-header">
          <div>
            <div className="pv-run-id">{row.Run}</div>
            <div className="pv-run-meta">
              {row.Environment && <span className="pv-badge pv-badge-env">{row.Environment}</span>}
              {row.Version     && <span className="pv-badge pv-badge-ver">v{row.Version}</span>}
              {row.Attempts    && <span className="pv-badge pv-badge-att">Attempt {row.Attempts}</span>}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body pv-body">

          {row.Metadata && (
            <div className="pv-section">
              <div className="pv-section-title">🧬 Metadata</div>
              <div className="pv-meta-grid">
                {[
                  ['Name',             row.Metadata.name],
                  ['Organism',         row.Metadata.organism],
                  ['Platform',         row.Metadata.platform],
                  ['Country',          row.Metadata.geo_loc_name_country_calc],
                  ['Continent',        row.Metadata.geo_loc_name_country_continent_calc],
                  ['Center',           row.Metadata.center_name],
                  ['Isolation Source', row.Metadata.isolation_source_sam],
                  ['Collection Date',  row.Metadata.Attributes?.collection_date_sam ?? row.Metadata.collection_date_sam],
                  ['Host',             row.Metadata.host_scientific_name_sam],
                  ['SRA File Date',    row.Metadata.run_file_create_date ? new Date(row.Metadata.run_file_create_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : null],
                ]
                  .filter(([, v]) => v && v !== 'missing')
                  .map(([label, val]) => (
                    <div className="pv-meta-item" key={label}>
                      <div className="pv-meta-label">{label}</div>
                      <div className="pv-meta-val">{String(val)}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="pv-section">
            <div className="pv-section-title">🕐 Run Timing</div>
            <div className="pv-grid-4">
              <div className="pv-stat"><div className="pv-stat-label">Start</div><div className="pv-stat-val">{fmtFull(row.RunStartDate)}</div></div>
              <div className="pv-stat"><div className="pv-stat-label">End</div><div className="pv-stat-val">{fmtFull(row.RunEndDate)}</div></div>
              <div className="pv-stat"><div className="pv-stat-label">Elapsed</div><div className="pv-stat-val pv-stat-highlight">{row.RunElapsedTime ?? '—'}s</div></div>
              <div className="pv-stat"><div className="pv-stat-label">Size</div><div className="pv-stat-val">{fmtBytes(row.Size)}</div></div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div className="pv-stat"><div className="pv-stat-label">SRA Release</div><div className="pv-stat-val">{fmtFull(row.SRAReleaseDate)}</div></div>
            </div>
          </div>

          <div className="pv-section">
            <div className="pv-section-title">🔬 Tool Results</div>
            <div className="pv-tool-grid">
              {Object.keys(bowtie2Anno).length > 0 && (
                <div className="pv-tool-card">
                  <div className="pv-tool-name">bowtie2</div>
                  <div className="pv-tool-row"><span className="pv-tool-key">ReadHits</span><span className={`pv-tool-bool ${bowtie2Anno.ReadHits === 'true' ? 'bool-true' : 'bool-false'}`}>{bowtie2Anno.ReadHits ?? '—'}</span></div>
                  <div className="pv-tool-row"><span className="pv-tool-key">ReadHitsCount</span><span className="pv-tool-num">{Number(bowtie2Anno.ReadHitsCount ?? 0).toLocaleString()}</span></div>
                </div>
              )}
              {Object.keys(seqtkAnno).length > 0 && (
                <div className="pv-tool-card">
                  <div className="pv-tool-name">seqtk(bowtie2)</div>
                  <div className="pv-tool-row"><span className="pv-tool-key">ReadHits</span><span className={`pv-tool-bool ${seqtkAnno.ReadHits === 'true' ? 'bool-true' : 'bool-false'}`}>{seqtkAnno.ReadHits ?? '—'}</span></div>
                  <div className="pv-tool-row"><span className="pv-tool-key">ReadHitsCount</span><span className="pv-tool-num">{Number(seqtkAnno.ReadHitsCount ?? 0).toLocaleString()}</span></div>
                </div>
              )}
              {Object.keys(blastNAnno).length > 0 && (
                <div className="pv-tool-card pv-tool-card-wide">
                  <div className="pv-tool-name">blastN(bowtie2)</div>
                  <div className="pv-tool-row"><span className="pv-tool-key">RegionHits</span><span className={`pv-tool-bool ${blastNAnno.RegionHits === 'true' ? 'bool-true' : 'bool-false'}`}>{blastNAnno.RegionHits ?? '—'}</span></div>
                  <div className="pv-tool-row"><span className="pv-tool-key">RegionHitsCount</span><span className="pv-tool-num">{Number(blastNAnno.RegionHitsCount ?? 0).toLocaleString()}</span></div>
                  {regionData && Object.keys(regionData).length > 0 && (
                    <div className="pv-region-breakdown">
                      <div className="pv-region-header">Region Breakdown</div>
                      {Object.entries(regionData)
                        .sort(([, a], [, b]) => b - a)
                        .map(([region, count]) => {
                          const pct = Math.min(100, (count / (blastNAnno.RegionHitsCount || 1)) * 100);
                          return (
                            <div className="pv-region-row" key={region}>
                              <span className="pv-region-name">{region}</span>
                              <span className="pv-region-bar-wrap"><span className="pv-region-bar" style={{ width: `${pct}%` }} /></span>
                              <span className="pv-region-count">{count.toLocaleString()}</span>
                              <span className="pv-region-pct">{pct.toFixed(1)}%</span>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
              {blastNSPAdes && <div className="pv-tool-card"><div className="pv-tool-name">blastN(SPAdes)</div><CollapsibleJSON label="View data" data={blastNSPAdes} /></div>}
              {spades       && <div className="pv-tool-card"><div className="pv-tool-name">SPAdes</div><CollapsibleJSON label="View data" data={spades} /></div>}
              {samtools     && <div className="pv-tool-card"><div className="pv-tool-name">samtools</div><CollapsibleJSON label="View data" data={samtools} /></div>}
            </div>
          </div>

          {latestAttempt && (
            <div className="pv-section">
              <div className="pv-section-title">⚙️ Workflow</div>
              <div className="pv-grid-3">
                {[
                  ['Run Name',   latestAttempt.RunName],
                  ['Profile',    latestAttempt.Profile],
                  ['Revision',   latestAttempt.Revision],
                  ['Commit',     latestAttempt.CommitId?.slice(0, 10)],
                  ['Resume',     latestAttempt.Resume],
                  ['Session ID', latestAttempt.SessionId],
                ].filter(([, v]) => v).map(([label, val]) => (
                  <div className="pv-stat" key={label}>
                    <div className="pv-stat-label">{label}</div>
                    <div className="pv-stat-val pv-mono">{val}</div>
                  </div>
                ))}
              </div>
              {latestAttempt.Repository && (
                <div style={{ marginTop: 10 }}>
                  <span className="pv-stat-label">Repository</span>{' '}
                  <a className="pv-link" href={latestAttempt.Repository} target="_blank" rel="noopener noreferrer">{latestAttempt.Repository}</a>
                </div>
              )}
            </div>
          )}

          {tasks.length > 0 && (
            <div className="pv-section">
              <div className="pv-section-title">📋 Tasks <span className="pv-section-count">({tasks.length})</span></div>
              <div className="pv-tasks">
                {tasks.map(([taskName, task]) => (
                  <div className="pv-task-card" key={taskName}>
                    <div className="pv-task-header">
                      <span className="pv-task-name">{taskName}</span>
                      <span className={`pv-task-status ${taskStatusClass(task.status)}`}>{task.status ?? 'unknown'}</span>
                    </div>
                    <div className="pv-task-metrics">
                      {task.seconds != null && <div className="pv-task-metric"><span className="pv-task-metric-icon">⏱</span><span>{fmtSeconds(task.seconds)}</span></div>}
                      {task.bytes != null && parseFloat(task.bytes) > 0 && <div className="pv-task-metric"><span className="pv-task-metric-icon">💾</span><span>{fmtBytes(task.bytes)}</span></div>}
                    </div>
                    {task.command && (
                      <div className="pv-task-command" title={task.command}>
                        {task.command.length > 130 ? task.command.slice(0, 130) + '…' : task.command}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
        <div className="modal-footer">
          <span />
          <div className="modal-footer-actions">
            <button className="btn-download" onClick={download}>↓ Download JSON</button>
            <button className="btn-close-modal" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  LARGE RUN FILTER MODAL
// ─────────────────────────────────────────────────────────────────────────────

const PIDENT_PRESETS = [
  { label: '75%',  value: 75,  desc: 'All alignments (default)' },
  { label: '80%',  value: 80,  desc: 'Likely poliovirus threshold' },
  { label: '90%',  value: 90,  desc: 'High confidence' },
  { label: '95%',  value: 95,  desc: 'Very high confidence' },
  { label: '96%',  value: 96,  desc: 'Poliovirus zone' },
];

const LEN_OPTIONS = [
  { label: 'Any',      value: 0   },
  { label: '≥ 50 bp',  value: 50  },
  { label: '≥ 100 bp', value: 100 },
  { label: '≥ 200 bp', value: 200 },
];

const POLTYPE_FALLBACK = ['PV1', 'PV2', 'PV3'];

function LargeRunModal({ data, onClose, onNavigate }) {
  const [minPident,        setMinPident]        = React.useState(75);
  const [minLen,           setMinLen]           = React.useState(0);
  const [filterPolytypes,  setFilterPolytypes]  = React.useState(false);
  const [polytypes,        setPolytypes]        = React.useState([]);
  const [filterSseqRegion,   setFilterSseqRegion]   = React.useState(false);
  const [sseqRegions,        setSseqRegions]        = React.useState([]);
  const [distinctData,       setDistinctData]       = React.useState(null);
  const [distinctLoading,    setDistinctLoading]    = React.useState(false);

  React.useEffect(() => {
    if (!data) return;
    setDistinctData(null);
    setDistinctLoading(true);
    axios
      .get(`${apiPath}/api/readhit/${data.run}/distinct`)
      .then((res) => {
        const d = res.data;
        setDistinctData(d);
        setPolytypes(d.polytypes?.length > 0 ? d.polytypes : POLTYPE_FALLBACK);
        setSseqRegions(d.sseqRegions ?? []);
      })
      .catch(() => {
        setPolytypes(POLTYPE_FALLBACK);
        setSseqRegions([]);
      })
      .finally(() => setDistinctLoading(false));
  }, [data?.run]);

  if (!data) return null;

  const availablePolytypes   = distinctData?.polytypes?.length  > 0 ? distinctData.polytypes  : POLTYPE_FALLBACK;
  const availableSseqRegions = distinctData?.sseqRegions ?? [];

  const togglePoltype    = (pt) => setPolytypes((p)   => p.includes(pt) ? p.filter((x) => x !== pt) : [...p, pt]);
  const toggleSseqRegion = (r)  => setSseqRegions((p) => p.includes(r)  ? p.filter((x) => x !== r)  : [...p, r]);

  const hasActiveFilters =
    minPident > 75 ||
    minLen > 0 ||
    (filterPolytypes  && polytypes.length   < availablePolytypes.length)   ||
    (filterSseqRegion && sseqRegions.length < availableSseqRegions.length  && sseqRegions.length > 0);

  const handleLoadFiltered = () => {
    const params = new URLSearchParams({ run: data.run });
    if (minPident > 75) params.set('minPident', String(minPident));
    if (minLen > 0) params.set('minLen', String(minLen));
    if (filterPolytypes && polytypes.length > 0 && polytypes.length < availablePolytypes.length)
      params.set('poltype', polytypes.join(','));
    if (filterSseqRegion && sseqRegions.length > 0 && sseqRegions.length < availableSseqRegions.length)
      params.set('sseqregion', sseqRegions.join(','));
    onNavigate(`/pass/ReadHits?${params.toString()}`);
    onClose();
  };

  const handleLoadAll = () => { onNavigate(`/pass/ReadHits?run=${data.run}`); onClose(); };

  const isDisabled =
    (filterPolytypes  && polytypes.length === 0) ||
    (filterSseqRegion && sseqRegions.length === 0);

  return (
    <div className="lrf-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lrf-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="lrf-header">
          <div className="lrf-header-left">
            <span className="lrf-warn-icon">⚠</span>
            <div>
              <div className="lrf-title">Large Dataset Detected</div>
              <div className="lrf-run-id">{data.run}</div>
            </div>
          </div>
          <button className="lrf-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Count bar ── */}
        <div className="lrf-count-bar">
          <div className="lrf-count-num">{Number(data.count).toLocaleString()}</div>
          <div className="lrf-count-label">alignment hits in this run</div>
          <div className="lrf-count-cap">
            Only the first <strong>75,000 rows</strong> will be loaded without filters.
            Use filters below to narrow the dataset and focus your analysis.
          </div>
        </div>

        {/* ── Distinct-values loading ── */}
        {distinctLoading && (
          <div className="lrf-distinct-loading">
            <span className="lrf-distinct-spinner" /> Loading filter options from dataset…
          </div>
        )}

        {/* ── Filters ── */}
        <div className="lrf-filters">

          {/* Min % Identity */}
          <div className="lrf-filter-group">
            <div className="lrf-filter-label">
              Min % Identity
              <span className="lrf-filter-val">{minPident}%</span>
              {distinctData?.pidentRange && (
                <span className="lrf-filter-hint">
                  &nbsp;(data: {distinctData.pidentRange.min}–{distinctData.pidentRange.max}%)
                </span>
              )}
            </div>
            <input
              type="range"
              className="lrf-slider"
              min={75} max={100} step={1}
              value={minPident}
              onChange={(e) => setMinPident(Number(e.target.value))}
            />
            <div className="lrf-presets">
              {PIDENT_PRESETS.map((p) => (
                <button
                  key={p.value}
                  className={`lrf-preset-btn${minPident === p.value ? ' active' : ''}`}
                  onClick={() => setMinPident(p.value)}
                  title={p.desc}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Min alignment length */}
          <div className="lrf-filter-group">
            <div className="lrf-filter-label">
              Min Alignment Length
              {distinctData?.lenRange && (
                <span className="lrf-filter-hint">
                  &nbsp;(data: {distinctData.lenRange.min}–{distinctData.lenRange.max} bp)
                </span>
              )}
            </div>
            <div className="lrf-radio-group">
              {LEN_OPTIONS.map((opt) => (
                <label key={opt.value} className="lrf-radio-item">
                  <input
                    type="radio"
                    name="lrf-minlen"
                    checked={minLen === opt.value}
                    onChange={() => setMinLen(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Poliovirus types — data-driven */}
          <div className="lrf-filter-group">
            <label className="lrf-toggle-row">
              <input
                type="checkbox"
                checked={filterPolytypes}
                onChange={(e) => setFilterPolytypes(e.target.checked)}
              />
              <span className="lrf-filter-label" style={{ marginBottom: 0 }}>
                Filter by Poliovirus Type
                {distinctData?.polytypes?.length > 0 && (
                  <span className="lrf-filter-hint"> ({distinctData.polytypes.length} unique)</span>
                )}
              </span>
            </label>
            {filterPolytypes && (
              <div className="lrf-checkbox-group">
                {availablePolytypes.map((pt) => (
                  <label key={pt} className={`lrf-check-item${polytypes.includes(pt) ? ' checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={polytypes.includes(pt)}
                      onChange={() => togglePoltype(pt)}
                    />
                    <span>{pt}</span>
                  </label>
                ))}
                {polytypes.length === 0 && (
                  <span className="lrf-check-warn">Select at least one type</span>
                )}
              </div>
            )}
          </div>

          {/* Gene region (sseqid prefix) — data-driven: 5UTR, 2C, VP1, VP2 … */}
          {!distinctLoading && availableSseqRegions.length > 0 && (
            <div className="lrf-filter-group">
              <label className="lrf-toggle-row">
                <input type="checkbox" checked={filterSseqRegion} onChange={(e) => setFilterSseqRegion(e.target.checked)} />
                <span className="lrf-filter-label" style={{ marginBottom: 0 }}>
                  Filter by Genome Region (sseqid)
                  <span className="lrf-filter-hint"> ({availableSseqRegions.length} regions in this run)</span>
                </span>
              </label>
              {filterSseqRegion && (
                <div className="lrf-checkbox-group lrf-sseqid-group">
                  <div className="lrf-sseqid-actions">
                    <button className="lrf-sseqid-btn" onClick={() => setSseqRegions([...availableSseqRegions])}>All</button>
                    <button className="lrf-sseqid-btn" onClick={() => setSseqRegions([])}>None</button>
                  </div>
                  {availableSseqRegions.map((r) => (
                    <label key={r} className={`lrf-check-item${sseqRegions.includes(r) ? ' checked' : ''}`}>
                      <input type="checkbox" checked={sseqRegions.includes(r)} onChange={() => toggleSseqRegion(r)} />
                      <span className="lrf-sseqid-label">{r}</span>
                    </label>
                  ))}
                  {sseqRegions.length === 0 && <span className="lrf-check-warn">Select at least one region</span>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="lrf-footer">
          <button className="lrf-btn-ghost" onClick={handleLoadAll} disabled={distinctLoading}>
            Load All <span className="lrf-btn-sub">(75k cap, no filters)</span>
          </button>
          <button
            className="lrf-btn-primary"
            onClick={handleLoadFiltered}
            disabled={isDisabled || distinctLoading}
          >
            {hasActiveFilters ? 'Apply Filters & View →' : 'View without Filters →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN DATATABLE VIEW
// ─────────────────────────────────────────────────────────────────────────────

// Parse initial state from ?runId= / ?timeFrame= or legacy ?filters= format
function parseInitState(search) {
  const result = { runId: '', timeFrame: '' };
  try {
    const params = new URLSearchParams(search);

    const runId = params.get('runId');
    if (runId) result.runId = decodeURIComponent(runId);

    const tf = params.get('timeFrame');
    if (tf) result.timeFrame = tf;

    // Legacy ?filters= JSON blob (MUI DataGrid format)
    if (!result.runId) {
      const fp = params.get('filters');
      if (fp) {
        const parsed = JSON.parse(decodeURIComponent(fp));
        result.runId = parsed?.items?.find((i) => i.field === 'Run')?.value ?? '';
      }
    }
  } catch {}
  return result;
}

// ── Export helpers ────────────────────────────────────────────────────────────

const getExportValue = (row, col) => {
  if (col.type === 'link') return row.Run ?? '';
  if (col.type === 'toolButton' || col.type === 'previewButton') return '';
  if (col.type === 'date') return fmtDate(row[col.key]);
  const v = col.accessor ? col.accessor(row) : row[col.key];
  return (v === null || v === undefined) ? '' : v;
};

const exportCSV = (rows, cols, filename) => {
  const exportCols = cols.filter(c => c.type !== 'toolButton' && c.type !== 'previewButton');
  const header = exportCols.map(c => `"${c.label}"`).join(',');
  const body = rows.map(row =>
    exportCols.map(col => {
      const v = String(getExportValue(row, col)).replace(/"/g, '""');
      return `"${v}"`;
    }).join(',')
  ).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename + '.csv';
  a.click(); URL.revokeObjectURL(url);
};

const exportExcel = (rows, cols, filename) => {
  const exportCols = cols.filter(c => c.type !== 'toolButton' && c.type !== 'previewButton');
  const header = exportCols.map(c => c.label);
  const data = rows.map(row => exportCols.map(col => getExportValue(row, col)));
  const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'PASS Data');
  XLSX.writeFile(wb, filename + '.xlsx');
};

function Datatable() {
  const navigate = useHistory();
  const location = useLocation();

  // Seed search and timeFrame from URL on first render (?runId= / ?timeFrame= from ReadHits back button)
  const initState = parseInitState(location.search);

  const [data,       setData]       = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [chartBuckets, setChartBuckets] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError,   setChartError]   = useState(null);

  const [timeFrame,      setTimeFrame]      = useState(initState.timeFrame || 'last24Hours');
  const [readHitsOnly,    setReadHitsOnly]    = useState(true);
  const [hideIncomplete,  setHideIncomplete]  = useState(false);
  const [regionHitsOnly,  setRegionHitsOnly]  = useState(true);
  const [filters,         setFilters]         = useState(EMPTY_FILTERS);
  const [filterOpen,     setFilterOpen]     = useState(false);
  const [metaOptions,    setMetaOptions]    = useState({ names: [], organisms: [], platforms: [], countries: [], centers: [], continents: [], isolationSources: [] });

  const [sortKey, setSortKey] = useState('RunStartDate');
  const [sortDir, setSortDir] = useState('desc');

  const [page,     setPage]     = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const [previewRow,     setPreviewRow]     = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError,   setPreviewError]   = useState(null);

  const [largeRunModal, setLargeRunModal] = useState(null);

  const [searchInput, setSearchInput] = useState(initState.runId);
  const [searchValue, setSearchValue] = useState(initState.runId);
  const searchTimer = useRef(null);

  // Clean the URL after seeding state from it
  useEffect(() => {
    if (location.search) {
      navigate.replace(location.pathname);
    }
  }, []); // eslint-disable-line

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchValue(val);
      setPage(0);
    }, 400);
  };

  const timeFrameConfig = useMemo(
    () => TIME_FRAMES.find((tf) => tf.key === timeFrame) ?? TIME_FRAMES[1],
    [timeFrame]
  );

  // ── Client-side incomplete filter ─────────────────────────────────────────
  // Hides rows with no RunEndDate AND no RunElapsedTime (still in-flight).
  // Pure client-side — never triggers a server refetch.
  const visibleData = useMemo(() => {
    if (!hideIncomplete) return data;
    return data.filter((row) =>
      !isEmpty(row.RunEndDate) || !isEmpty(row.RunElapsedTime)
    );
  }, [data, hideIncomplete]);

  // ── Reset page to 0 on any filter/sort/time change ────────────────────────
  // hideIncomplete intentionally excluded — it's client-side only.
  useEffect(() => {
    setPage(0);
  }, [timeFrame, readHitsOnly, filters, sortKey, sortDir, pageSize, searchValue]);

  // ── Fetch: METADATA OPTIONS (for filter dropdowns) ────────────────────────
  useEffect(() => {
    axios.get(`${apiPath}/api/dashboard-status/metadata-counts`).then(res => {
      const d = res.data;
      setMetaOptions({
        names:            (d.names            || []).map(x => x.label),
        organisms:        (d.organisms        || []).map(x => x.label),
        platforms:        (d.platforms        || []).map(x => x.label),
        countries:        (d.countries        || []).map(x => x.label),
        centers:          (d.centerNames      || []).map(x => x.label),
        continents:       (d.continents       || []).map(x => x.label),
        isolationSources: (d.isolationSources || []).map(x => x.label),
      });
    }).catch(() => {});
  }, []);

  // ── Fetch: TABLE ROWS ─────────────────────────────────────────────────────
  // cachedTotal is passed on page 2+ so the server can skip countDocuments().
  // When page resets to 0 (any filter change), totalCount is also reset to 0
  // by setData([]) / the new response, so cachedTotal naturally becomes 0
  // and the server counts fresh.
  // hideIncomplete intentionally excluded from deps — no server refetch needed.
  useEffect(() => {
    const { signal, abort } = makeAbortable();

    const params = buildQueryParams({
      timeFrameKey: timeFrame,
      filters:      { ...filters, blastNRegionHits: filters.blastNRegionHits || regionHitsOnly, runId: searchValue },
      readHitsOnly,
      page,
      pageSize,
      sortKey,
      sortDir,
      cachedTotal:  totalCount,  // 0 on first load / after any filter reset
    });

    setLoading(true);
    setFetchError(null);

    axios
      .get(`${apiPath}/api/data?${params.toString()}`, { signal })
      .then((res) => {
        const payload  = res.data;
        const bodyRows = Array.isArray(payload) ? payload : payload?.data;
        const rows     = Array.isArray(bodyRows) ? bodyRows : [];

        const bodyTotal = payload?.pagination?.totalCount;
        const bodyPages = payload?.pagination?.totalPages;

        const total = Number(bodyTotal ?? res.headers['x-total-count'] ?? rows.length);
        const pages = Number(bodyPages ?? res.headers['x-total-pages'] ?? Math.ceil(total / pageSize)) || 1;

        setData(rows);
        setTotalCount(total);
        setTotalPages(pages);
      })
      .catch((err) => {
        ignoreAbort(err);
        const status  = err?.response?.status ?? 0;
        const message = err?.response?.data?.error ?? err?.message ?? 'Unknown error';
        console.error(`[Datatable] /api/data failed (${status}):`, message);
        setFetchError({ status, message });
        setData([]);
      })
      .finally(() => setLoading(false));

    return abort;
  }, [timeFrame, readHitsOnly, regionHitsOnly, filters, searchValue, page, pageSize, sortKey, sortDir]);

  // ── Fetch: AGGREGATION CHART ──────────────────────────────────────────────
  useEffect(() => {
    const { signal, abort } = makeAbortable();
    const { granularity } = timeFrameConfig;
    const params = buildAggregateParams(timeFrame, granularity, readHitsOnly, {
      ...filters, runId: searchValue,
    });

    setChartLoading(true);
    setChartError(null);

    axios
      .get(`${apiPath}/api/data/aggregate?${params.toString()}`, { signal })
      .then((res) => {
        const raw     = Array.isArray(res.data) ? res.data : [];
        const labeled = raw.map((b) => ({ ...b, label: formatBucketLabel(b.bucket, granularity) }));
        setChartBuckets(fillGaps(labeled, granularity));
      })
      .catch((err) => {
        ignoreAbort(err);
        setChartError(err?.response?.data?.error ?? err?.message ?? 'Chart fetch failed');
        setChartBuckets([]);
      })
      .finally(() => setChartLoading(false));

    return abort;
  }, [timeFrame, readHitsOnly, filters, searchValue, timeFrameConfig]);

  // ── Sort handler ──────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (!col.sortable) return;
    const key = col.serverKey ?? col.key;
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const thClass = (col) => {
    if (!col.sortable) return '';
    const key = col.serverKey ?? col.key;
    return sortKey === key ? `sortable sort-${sortDir}` : 'sortable';
  };

  const handleToolClick = (row, col) => {
    if (col.endpoint === 'ReadHits') {
      const count = Number(nested(row, 'blastN(bowtie2).PolioAnno.RegionHitsCount') ?? 0);
      if (count > 75000) {
        setLargeRunModal({ run: row.Run, count });
        return;
      }
    }
    navigate.push(`/pass/${col.endpoint}?run=${row.Run}&timeFrame=${timeFrame}`);
  };

  const handleOpenPreview = async (runId) => {
    setPreviewRow(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${apiPath}/api/data/run/${encodeURIComponent(runId)}`);
      setPreviewRow(res.data);
    } catch (err) {
      setPreviewError({ runId, message: err?.response?.data?.error ?? err?.message ?? 'Failed to load' });
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Visible columns — driven off visibleData ──────────────────────────────
  const visibleCols = useMemo(
    () => COLUMN_DEFS.filter(
      (c) => !c.optional || visibleData.some((r) => !isEmpty(c.accessor?.(r)))
    ),
    [visibleData]
  );

  const renderCell = (row, col) => {
    switch (col.type) {
      case 'link':
        return <a className="run-link" href={`${sraUrl}${row.Run}`} target="_blank" rel="noopener noreferrer">{row.Run ?? 'N/A'}</a>;
      case 'date':
        return <span className="mono-val">{fmtDate(row[col.key])}</span>;
      case 'mono': {
        const val = col.accessor ? col.accessor(row) : row[col.key];
        return <span className="mono-val">{isEmpty(val) ? '—' : val}</span>;
      }
      case 'toolButton': {
        const has = hasToolData(col.accessor(row));
        return (
          <button className={`btn-view ${has ? 'active' : 'inactive'}`} disabled={!has} onClick={() => has && handleToolClick(row, col)}>
            View
          </button>
        );
      }
      case 'previewButton':
        return <button className="btn-preview" onClick={() => handleOpenPreview(row.Run)}>⊞ Preview</button>;
      default: {
        const v = row[col.key];
        return <span className="mono-val">{isEmpty(v) ? '—' : String(v)}</span>;
      }
    }
  };

  const activeFilterCount = countActiveFilters({ ...filters, runId: searchValue });
  const filterTags        = buildFilterTags(
    { ...filters, runId: searchValue },
    (patch) => {
      if ('runId' in patch) { setSearchInput(patch.runId ?? ''); setSearchValue(patch.runId ?? ''); }
      setFilters((f) => ({ ...f, ...patch }));
    }
  );
  const pageButtons  = useMemo(() => buildPageButtons(totalPages, page), [totalPages, page]);
  const hiddenCount  = data.length - visibleData.length;

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="dt-root">

        {/* ── TOP BAR ── */}
        <div className="dt-topbar">
          <span className="dt-brand">PASS<span>/datatable</span></span>
          <div className="dt-topbar-sep" />

          {TIME_FRAMES.map((tf) => (
            <button
              key={tf.key}
              className={`pill ${timeFrame === tf.key ? 'active' : ''}`}
              onClick={() => setTimeFrame(tf.key)}
              title={`Aggregation: ${granularityLabel(tf.granularity)}`}
            >{tf.label}</button>
          ))}

          <div className="dt-topbar-sep" />

          <button
            className={`pill ${readHitsOnly ? 'active-green' : ''}`}
            onClick={() => setReadHitsOnly((v) => !v)}
            title="Only show runs with blastN(bowtie2) RegionHitsCount > 0 (i.e. likely contain poliovirus reads)"
          >
            {readHitsOnly ? '✓' : '○'} ReadHits Filter
          </button>

          <button
            className={`pill ${hideIncomplete ? 'active-green' : ''}`}
            onClick={() => setHideIncomplete((v) => !v)}
            title="Hide rows where RunEndDate and RunElapsedTime are both missing (in-flight runs)"
          >
            {hideIncomplete ? '✓' : '○'} Hide Incomplete
          </button>

          <button
            className={`pill ${regionHitsOnly ? 'active-green' : ''}`}
            onClick={() => setRegionHitsOnly((v) => !v)}
            title="Only show runs where blastN(bowtie2) RegionHits = true"
          >
            {regionHitsOnly ? '✓' : '○'} Region Hits
          </button>

          <button
            className={`pill filter-btn ${activeFilterCount > 0 ? 'has-filters' : ''}`}
            onClick={() => setFilterOpen(true)}
          >
            ⚡ Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>

          {activeFilterCount > 0 && (
            <button className="pill danger" onClick={() => {
              setFilters(EMPTY_FILTERS);
              setSearchInput('');
              setSearchValue('');
            }}>✕ Clear All</button>
          )}
        </div>

        {/* ── STATS BAR ── */}
        <div className="dt-statsbar">
          <div className="stat-item">
            <span className={`stat-dot ${!loading ? 'live' : ''}`} />
            {loading ? 'Fetching…' : 'Live'}
          </div>
          <div className="stat-item">
            Total: <span className="stat-val">{totalCount.toLocaleString()}</span>
          </div>
          {hideIncomplete && hiddenCount > 0 && (
            <div className="stat-item">
              Showing: <span className="stat-val">{visibleData.length.toLocaleString()}</span>
              <span style={{ color: 'var(--dt-amber)', fontSize: 10, marginLeft: 4 }}>
                ({hiddenCount} incomplete hidden)
              </span>
            </div>
          )}
          <div className="stat-item">
            Page: <span className="stat-val">{page + 1}</span> / {totalPages || 1}
          </div>
          <div className="stat-item">
            Aggregation: <span className="stat-val">{granularityLabel(timeFrameConfig.granularity)}</span>
          </div>
          <div className="stat-item">
            Rows/page:&nbsp;
            <select className="page-size-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="stat-item" style={{ marginLeft: 'auto', gap: 6 }}>
            <button
              className="pill"
              disabled={visibleData.length === 0}
              onClick={() => exportCSV(visibleData, visibleCols, `pass_data_${timeFrame}_p${page + 1}`)}
              title="Download current page as CSV"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              ⬇ CSV
            </button>
            <button
              className="pill"
              disabled={visibleData.length === 0}
              onClick={() => exportExcel(visibleData, visibleCols, `pass_data_${timeFrame}_p${page + 1}`)}
              title="Download current page as Excel (.xlsx)"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              ⬇ Excel
            </button>
          </div>
        </div>

        {/* ── SEARCH + FILTER TAGS ── */}
        <div className="dt-searchbar">
          <div className="search-wrap">
            <span className="search-icon">⌕</span>
            <input
              className="search-input"
              placeholder="Search Run ID… (server-side)"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          {filterTags.length > 0 && (
            <div className="active-filters">
              {filterTags.map((tag, i) => (
                <span className="filter-tag" key={i}>
                  {tag.label}<button onClick={tag.clear}>✕</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── TABLE ── */}
        <div className="dt-table-wrap">
          {loading ? (
            <div className="dt-empty"><div className="dt-spinner" /><span>Fetching data…</span></div>
          ) : fetchError ? (
            <div className="dt-error-banner">
              <div className="dt-error-icon">⚠</div>
              <div className="dt-error-body">
                <div className="dt-error-title">Failed to load data {fetchError.status ? `(HTTP ${fetchError.status})` : ''}</div>
                <div className="dt-error-msg">{fetchError.message}</div>
                <div className="dt-error-hint">Try a different time range or toggle the ReadHits filter off.</div>
              </div>
            </div>
          ) : visibleData.length === 0 ? (
            <div className="dt-empty">
              <div className="dt-empty-icon">◎</div>
              <span>
                {data.length > 0 && hideIncomplete
                  ? `All ${data.length} rows on this page were incomplete and hidden`
                  : 'No rows match the current filters'}
              </span>
            </div>
          ) : (
            <table className="dt-table">
              <thead>
                <tr>
                  {visibleCols.map((col) => (
                    <th
                      key={col.key}
                      className={thClass(col)}
                      onClick={() => handleSort(col)}
                      style={{ cursor: col.sortable ? 'pointer' : 'default' }}
                    >{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleData.map((row, ri) => (
                  <tr key={row.Run ?? `row-${ri}`}>
                    {visibleCols.map((col) => (
                      <td key={col.key}>{renderCell(row, col)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── PAGINATION ── */}
        {!loading && totalCount > 0 && (
          <div className="dt-pagination">
            <span>
              Showing {(page * pageSize + 1).toLocaleString()}–
              {Math.min((page + 1) * pageSize, totalCount).toLocaleString()} of{' '}
              {totalCount.toLocaleString()} rows
              {hideIncomplete && hiddenCount > 0 && (
                <span style={{ color: 'var(--dt-amber)', marginLeft: 6 }}>
                  ({visibleData.length} visible this page)
                </span>
              )}
            </span>
            <div className="page-btns">
              <button className="page-btn" disabled={page === 0} onClick={() => setPage(0)}>«</button>
              <button className="page-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹</button>
              {pageButtons.map((b, i) =>
                b === '…' ? (
                  <span className="page-ellipsis" key={`e${i}`}>…</span>
                ) : (
                  <button key={b} className={`page-btn ${b === page ? 'current' : ''}`} onClick={() => setPage(b)}>
                    {b + 1}
                  </button>
                )
              )}
              <button className="page-btn" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>›</button>
              <button className="page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* ── FILTER MODAL ── */}
      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        onApply={(f) => { setFilters(f); setPage(0); }}
        totalCount={totalCount}
        metaOptions={metaOptions}
        fetchPreviewCount={async (localFilters) => {
          const params = buildQueryParams({
            timeFrameKey: timeFrame,
            filters: { ...localFilters, runId: searchValue },
            readHitsOnly,
            page: 0,
            pageSize: 1,
            sortKey: 'RunStartDate',
            sortDir: 'desc',
            cachedTotal: 0,
          });
          const res = await axios.get(`${apiPath}/api/data?${params.toString()}`);
          return res.data?.totalCount ?? (Array.isArray(res.data?.data) ? res.data.data.length : 0);
        }}
      />

      {/* ── PREVIEW LOADING ── */}
      {previewLoading && (
        <div className="modal-overlay">
          <div className="preview-loading-box">
            <div className="dt-spinner" />
            <span>Loading run details…</span>
          </div>
        </div>
      )}

      {/* ── PREVIEW ERROR ── */}
      {previewError && (
        <div className="modal-overlay" onClick={() => setPreviewError(null)}>
          <div className="modal-box" style={{ width: 'min(480px, 94vw)', padding: 0 }}>
            <div className="modal-header">
              <span className="modal-title" style={{ color: 'var(--dt-red)' }}>⚠ Preview Failed</span>
              <button className="modal-close" onClick={() => setPreviewError(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontFamily: 'var(--dt-sans)', fontSize: 13, color: 'var(--dt-text)', marginBottom: 8 }}>
                Could not load <strong style={{ fontFamily: 'var(--dt-mono)' }}>{previewError.runId}</strong>.
              </p>
              <p style={{ fontFamily: 'var(--dt-mono)', fontSize: 11, color: 'var(--dt-muted)' }}>{previewError.message}</p>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn-close-modal" onClick={() => setPreviewError(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── PREVIEW MODAL ── */}
      {previewRow && <PreviewModal row={previewRow} onClose={() => setPreviewRow(null)} />}

      {/* ── LARGE RUN FILTER MODAL ── */}
      <LargeRunModal
        data={largeRunModal}
        onClose={() => setLargeRunModal(null)}
        onNavigate={(path) => navigate.push(path)}
      />
    </>
  );
}

export default Datatable;